import { SelectFieldProps } from 'components/fields/select-fields/select-filed';

import {
  SingleSelectValue,
  MultiSelectValue,
  SelectValue,
} from 'components/common/select/select-types';
import { fromBase64, toBase64 } from 'helpers';
import { toXsltHtml } from 'helpers/xslt-html.helper';
import { AppDocument } from 'store/api/types';
import { processXslt } from 'utils/xslt-transform.helper';

export const isSingleSelectValue = (value: unknown): value is SingleSelectValue =>
  typeof value === 'object' && value !== null && 'value' in value && 'label' in value;

export const isMultiSelectValue = (value: unknown): value is MultiSelectValue => {
  if (Array.isArray(value)) {
    if (value.length > 0) {
      return 'label' in value[0] && 'value' in value[0];
    }

    return true;
  }

  return false;
};

export const isSelectValue = (value: unknown): value is SelectValue =>
  isMultiSelectValue(value) || isSingleSelectValue(value);

export const getSelectValue = ({
  value,
  options,
  onlyValue,
}: Pick<SelectFieldProps, 'onlyValue' | 'options'> & {
  value: string | string[] | SelectValue;
}): SelectValue => {
  if (!onlyValue && isSelectValue(value)) return value;
  const getOption = (search?: any): SingleSelectValue => {
    let option = options?.find(
      (item) => !Array.isArray(item) && (item as any).value === search,
    );
    if (!option && search) {
      option = { label: search, value: search };
    }
    if (isSingleSelectValue(option)) return option;
  };
  if (Array.isArray(value)) {
    return value?.map((item) => getOption(item)) as MultiSelectValue;
  }
  return getOption(value) ?? null;
};

const TBD_PREVIEW_TYPES = ['base', 'agreement_base'] as const;

const tbdTypeOf = (item?: { type?: string | null } | null) =>
  String(item?.type ?? '').toLowerCase();

export const getDocumentContent = (document: AppDocument) => {
  return document?.tbd?.find((item) => tbdTypeOf(item) === 'base')?.content;
};

export const getDocumentSignData = (document?: AppDocument | null) => {
  return document?.tbs;
};

const looksLikeHtml = (markup: string) => {
  const head = markup.trimStart().slice(0, 200).toLowerCase();
  return (
    head.startsWith('<!doctype html') ||
    head.startsWith('<html') ||
    head.startsWith('<body')
  );
};

const decodeDocumentMarkup = (raw: string) => {
  if (raw.trim().startsWith('<')) {
    return raw;
  }

  try {
    return fromBase64(raw);
  } catch {
    return raw;
  }
};

/** `tbd[base]` / `tbd[agreement_base]` is either raw markup or base64. Preview screens need both shapes. */
export const getDocumentPreview = (document?: AppDocument | null) => {
  const parts = document?.tbd ?? [];
  const candidates = TBD_PREVIEW_TYPES.map(
    (type) => parts.find((item) => tbdTypeOf(item) === type)?.content,
  ).filter((content): content is string => Boolean(content));

  if (!candidates.length) {
    return { html: '', xsltBase64: null as string | null };
  }

  const decoded = candidates.map((raw) => ({
    raw,
    markup: decodeDocumentMarkup(raw),
  }));

  const htmlPart = decoded.find((item) => looksLikeHtml(item.markup));
  const chosen =
    htmlPart ??
    decoded.find((item) => item.markup.includes('<xsl:stylesheet')) ??
    decoded[0];
  const xsltBase64 = chosen.raw.trim().startsWith('<') ? toBase64(chosen.raw) : chosen.raw;

  // Реальный NDA/договор: HTML в tbd[base]. XMLDSIG из моков несёт свою XSL.
  // HTML нельзя гонять через processXslt — парсер XML на мегабайтах падает
  // в «Произошла ошибка при открытии документа».
  if (looksLikeHtml(chosen.markup)) {
    return { html: chosen.markup, xsltBase64 };
  }

  if (chosen.markup.includes('<xsl:stylesheet')) {
    const transformed = toXsltHtml(processXslt(chosen.markup));
    if (transformed) {
      return { html: transformed, xsltBase64 };
    }
  }

  return { html: chosen.markup, xsltBase64 };
};
